/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `StoreFinderProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface StoreFinderProps {
    /**
     * A string representing the CSS class to be applied to the StoreFinderIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#141414'
     */
    color1?: string;
    /**
     * A number or string representing the height of the StoreFinderIcon element.
     *
     * @default 50
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'StoreFinder'
     */
    testId?: string;
    /**
     * A number or string representing the height of the StoreFinderIcon element.
     *
     * @default 51
     */
    width?: number | string;
}

/**
 * The `StoreFinder` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `StoreFinderProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the StoreFinderIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the StoreFinderIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the StoreFinderIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const StoreFinderComponent = <StoreFinder className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const StoreFinder = ({className = '', color1 = '#141414', height = 50, ref, testId = 'StoreFinder', width = 51}: StoreFinderProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 50 51"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <rect fill="none" height="48.5" rx="24.25" stroke={color1} strokeWidth="1.5" width="48.5" x="0.75" y="1.3457" />
        <path d="M17.3325 25.7396C16.8476 25.5511 16.6051 25.4568 16.5344 25.3209C16.473 25.2031 16.4729 25.0629 16.5341 24.945C16.6048 24.8091 16.8471 24.7145 17.3318 24.5253L31.0866 19.1576C31.5241 18.9869 31.7429 18.9015 31.8827 18.9482C32.0041 18.9888 32.0993 19.084 32.1399 19.2054C32.1866 19.3452 32.1012 19.564 31.9305 20.0015L26.5628 33.7563C26.3736 34.241 26.279 34.4833 26.1431 34.554C26.0253 34.6152 25.885 34.6151 25.7672 34.5537C25.6313 34.483 25.537 34.2405 25.3485 33.7556L23.2079 28.2513C23.1696 28.1529 23.1505 28.1037 23.1209 28.0622C23.0947 28.0255 23.0626 27.9934 23.0259 27.9672C22.9844 27.9376 22.9352 27.9185 22.8368 27.8802L17.3325 25.7396Z" fill="none" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.70198" />
    </svg>
);

StoreFinder.displayName = 'StoreFinder';